import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";

import {
  FaPhoneAlt,
  FaEnvelope,
  FaWhatsapp,
  FaMapMarkerAlt,
  FaArrowRight,
  FaCheckCircle,
  FaClock,
  FaPaperPlane,
  FaStar,
} from "react-icons/fa";

export default function Contact() {
  const form = useRef(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    AOS.init({
      duration: 750,
      once: true,
      easing: "ease-out-cubic",
      offset: 70,
    });

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();

    const formData = new FormData(form.current);

    const name = formData.get("user_name");
    const email = formData.get("user_email");
    const message = formData.get("message");

    const whatsappMessage = `Hello PrimeAppConnect,

I would like to discuss a project.

Name: ${name}
Email: ${email}

Project Details:
${message}`;

    const whatsappUrl = `https://wa.me/916301898060?text=${encodeURIComponent(
      whatsappMessage
    )}`;

    setSending(true);

    setTimeout(() => {
      window.open(whatsappUrl, "_blank", "noopener,noreferrer");
      form.current.reset();
      setSending(false);
    }, 500);
  };

  const contactItems = [
    {
      icon: <FaPhoneAlt />,
      label: "Phone",
      value: "+91 63018 98060",
      href: "tel:+916301898060",
    },
    {
      icon: <FaEnvelope />,
      label: "Email",
      value: "primeappconnect@gmail.com",
      href: "mailto:primeappconnect@gmail.com",
    },
    {
      icon: <FaWhatsapp />,
      label: "WhatsApp",
      value: "Chat with our team",
      href: "https://wa.me/916301898060",
    },
  ];

  return (
    <main className="relative min-h-screen overflow-hidden bg-[#f8fbff] px-5 pb-24 pt-28 font-[Manrope] text-slate-900 sm:px-8 lg:px-12">
      {/* Background */}
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        {/* Grid */}
        <div
          className="absolute inset-0 opacity-70"
          style={{
            backgroundImage:
              "linear-gradient(rgba(37,99,235,.045) 1px, transparent 1px), linear-gradient(90deg, rgba(37,99,235,.045) 1px, transparent 1px)",
            backgroundSize: "70px 70px",
            maskImage:
              "linear-gradient(to bottom, black 0%, black 45%, transparent 90%)",
          }}
        />

        {/* Ambient glow */}
        <div className="absolute -left-40 top-[5%] h-[420px] w-[420px] animate-pulse rounded-full bg-blue-200/30 blur-[110px]" />

        <div
          className="absolute -right-48 top-[35%] h-[450px] w-[450px] rounded-full bg-sky-200/25 blur-[110px]"
          style={{ animation: "pulse 8s ease-in-out infinite" }}
        />

        <div className="absolute bottom-[-120px] left-[35%] h-[350px] w-[350px] rounded-full bg-indigo-200/20 blur-[110px]" />

        {/* Floating dots */}
        <span className="absolute left-[10%] top-[17%] h-1.5 w-1.5 animate-bounce rounded-full bg-blue-500/40" />
        <span className="absolute right-[15%] top-[27%] h-1 w-1 animate-ping rounded-full bg-blue-500/40" />
        <span className="absolute bottom-[20%] left-[18%] h-1.5 w-1.5 animate-bounce rounded-full bg-blue-500/40" />
      </div>

      <div className="mx-auto max-w-7xl">
        {/* Hero */}
        <section
          className="mx-auto max-w-4xl text-center"
          data-aos="fade-up"
        >
          <div className="inline-flex animate-pulse items-center gap-2 rounded-full border border-blue-200 bg-white px-4 py-2 text-[10px] font-extrabold tracking-[0.22em] text-blue-600 shadow-sm">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-600" />
            </span>
            CONTACT PRIMEAPPCONNECT
          </div>

          <h1 className="mt-6 text-4xl font-extrabold leading-[1.08] tracking-[-0.045em] text-slate-950 sm:text-5xl lg:text-[3.8rem]">
            Let's Build Something
            <span className="mt-1 block bg-gradient-to-r from-blue-600 via-sky-500 to-blue-600 bg-[length:200%_auto] bg-clip-text text-transparent">
              Great Together
            </span>
          </h1>

          <p className="mx-auto mt-6 max-w-2xl text-sm leading-7 text-slate-500 sm:text-base">
            Have a project in mind, need a website, mobile app, marketing
            solution or custom business system? Tell us what you need.
          </p>

          <div className="mt-7 flex flex-wrap justify-center gap-x-7 gap-y-3 text-xs font-semibold text-slate-500">
            <span className="flex items-center gap-2">
              <FaCheckCircle className="text-blue-600" />
              Quick Response
            </span>

            <span className="flex items-center gap-2">
              <FaCheckCircle className="text-blue-600" />
              Business Focused
            </span>

            <span className="flex items-center gap-2">
              <FaCheckCircle className="text-blue-600" />
              Direct WhatsApp
            </span>
          </div>
        </section>

        {/* Main Contact */}
        <section className="mt-16 grid gap-7 lg:grid-cols-[0.82fr_1.18fr]">
          {/* Contact Information */}
          <div
            data-aos="fade-right"
            className="group relative overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-7 shadow-[0_20px_70px_rgba(15,23,42,.07)] transition duration-500 hover:-translate-y-1 hover:shadow-[0_30px_80px_rgba(37,99,235,.10)] sm:p-9"
          >
            <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-blue-100/70 blur-[90px] transition duration-700 group-hover:scale-125" />

            <div className="relative">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-600 transition duration-500 group-hover:rotate-3 group-hover:scale-110">
                <FaPaperPlane />
              </div>

              <span className="mt-7 block text-[10px] font-extrabold tracking-[0.22em] text-blue-600">
                GET IN TOUCH
              </span>

              <h2 className="mt-4 text-3xl font-extrabold leading-tight tracking-[-0.035em] text-slate-950">
                Let's Talk About
                <span className="block bg-gradient-to-r from-blue-600 to-sky-500 bg-clip-text text-transparent">
                  Your Project
                </span>
              </h2>

              <p className="mt-5 text-sm leading-7 text-slate-500">
                Whether you need a website, mobile application, CRM, marketing
                campaign or a complete digital solution, we're ready to
                understand your requirements.
              </p>

              {/* Contact Cards */}
              <div className="mt-8 space-y-3">
                {contactItems.map((item, index) => (
                  <a
                    key={item.label}
                    href={item.href}
                    target={
                      item.label === "WhatsApp" ? "_blank" : undefined
                    }
                    rel={
                      item.label === "WhatsApp"
                        ? "noopener noreferrer"
                        : undefined
                    }
                    data-aos="fade-up"
                    data-aos-delay={index * 80}
                    className="group flex items-center gap-4 rounded-2xl border border-slate-200 bg-slate-50/80 p-4 transition-all duration-300 hover:-translate-y-1 hover:border-blue-200 hover:bg-white hover:shadow-[0_15px_35px_rgba(37,99,235,.08)]"
                  >
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white text-blue-600 shadow-sm transition duration-300 group-hover:scale-110 group-hover:bg-blue-600 group-hover:text-white">
                      {item.icon}
                    </span>

                    <div className="min-w-0">
                      <p className="text-[10px] font-extrabold uppercase tracking-[0.15em] text-slate-400">
                        {item.label}
                      </p>

                      <p className="mt-1 truncate text-sm font-bold text-slate-700 transition group-hover:text-blue-600">
                        {item.value}
                      </p>
                    </div>

                    <FaArrowRight className="ml-auto shrink-0 text-xs text-slate-300 transition duration-300 group-hover:translate-x-1 group-hover:text-blue-600" />
                  </a>
                ))}
              </div>

              {/* Location */}
              <div className="mt-7 border-t border-slate-100 pt-7">
                <div className="flex items-start gap-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                    <FaMapMarkerAlt />
                  </span>

                  <div>
                    <p className="text-[10px] font-extrabold uppercase tracking-[0.15em] text-slate-400">
                      Our Location
                    </p>

                    <p className="mt-1 text-sm font-semibold leading-6 text-slate-600">
                      13.559823, 78.503698
                    </p>

                    <a
                      href="https://maps.app.goo.gl/UdfLXDpf4Nyc2B699"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-flex items-center gap-2 text-xs font-extrabold text-blue-600 transition hover:text-blue-700"
                    >
                      Open in Google Maps
                      <FaArrowRight className="text-[9px]" />
                    </a>
                  </div>
                </div>
              </div>

              {/* Availability */}
              <div className="mt-7 flex items-center gap-3 rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-emerald-600 shadow-sm">
                  <FaClock className="text-sm" />
                </span>

                <div>
                  <p className="text-xs font-extrabold text-slate-700">
                    Support Available
                  </p>

                  <p className="mt-0.5 text-[11px] text-slate-500">
                    We're available to discuss your requirements.
                  </p>
                </div>

                <span className="relative ml-auto flex h-2.5 w-2.5">
                  <span className="absolute h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                  <span className="relative h-2.5 w-2.5 rounded-full bg-emerald-500" />
                </span>
              </div>
            </div>
          </div>

          {/* Form */}
          <div
            data-aos="fade-left"
            className="relative overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-7 shadow-[0_20px_70px_rgba(15,23,42,.07)] transition duration-500 hover:shadow-[0_30px_80px_rgba(37,99,235,.10)] sm:p-9"
          >
            <div className="absolute -bottom-32 -right-32 h-80 w-80 rounded-full bg-blue-100/60 blur-[100px]" />

            <div className="relative">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <span className="text-[10px] font-extrabold tracking-[0.22em] text-blue-600">
                    START A CONVERSATION
                  </span>

                  <h2 className="mt-4 text-3xl font-extrabold leading-tight tracking-[-0.035em] text-slate-950">
                    Tell Us About
                    <span className="block bg-gradient-to-r from-blue-600 to-sky-500 bg-clip-text text-transparent">
                      Your Project
                    </span>
                  </h2>
                </div>

                <div className="hidden h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-600 sm:flex">
                  <FaPaperPlane />
                </div>
              </div>

              <p className="mt-4 max-w-xl text-sm leading-7 text-slate-500">
                Share a few details and we'll continue the conversation
                directly on WhatsApp.
              </p>

              <form
                ref={form}
                onSubmit={handleSubmit}
                className="mt-8 space-y-5"
              >
                {/* Name */}
                <div>
                  <label
                    htmlFor="user_name"
                    className="mb-2 block text-xs font-extrabold text-slate-600"
                  >
                    Your Name
                  </label>

                  <input
                    id="user_name"
                    type="text"
                    name="user_name"
                    required
                    autoComplete="name"
                    placeholder="Enter your name"
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm font-medium text-slate-800 outline-none placeholder:text-slate-400 transition duration-300 hover:border-slate-300 hover:bg-white focus:-translate-y-0.5 focus:border-blue-400 focus:bg-white focus:ring-4 focus:ring-blue-500/10"
                  />
                </div>

                {/* Email */}
                <div>
                  <label
                    htmlFor="user_email"
                    className="mb-2 block text-xs font-extrabold text-slate-600"
                  >
                    Email Address
                  </label>

                  <input
                    id="user_email"
                    type="email"
                    name="user_email"
                    required
                    autoComplete="email"
                    placeholder="you@example.com"
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm font-medium text-slate-800 outline-none placeholder:text-slate-400 transition duration-300 hover:border-slate-300 hover:bg-white focus:-translate-y-0.5 focus:border-blue-400 focus:bg-white focus:ring-4 focus:ring-blue-500/10"
                  />
                </div>

                {/* Message */}
                <div>
                  <label
                    htmlFor="message"
                    className="mb-2 block text-xs font-extrabold text-slate-600"
                  >
                    Project Details
                  </label>

                  <textarea
                    id="message"
                    name="message"
                    rows={6}
                    required
                    placeholder="Tell us what you want to build..."
                    className="w-full resize-none rounded-xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm font-medium leading-6 text-slate-800 outline-none placeholder:text-slate-400 transition duration-300 hover:border-slate-300 hover:bg-white focus:-translate-y-0.5 focus:border-blue-400 focus:bg-white focus:ring-4 focus:ring-blue-500/10"
                  />
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={sending}
                  className="group relative flex w-full items-center justify-center gap-3 overflow-hidden rounded-xl bg-blue-600 px-6 py-4 text-sm font-extrabold text-white shadow-[0_14px_35px_rgba(37,99,235,.18)] transition-all duration-300 hover:-translate-y-1 hover:bg-blue-700 hover:shadow-[0_20px_45px_rgba(37,99,235,.25)] disabled:cursor-wait disabled:opacity-70"
                >
                  <span className="absolute inset-0 -translate-x-full skew-x-[-20deg] bg-white/15 transition duration-700 group-hover:translate-x-[150%]" />

                  {sending ? (
                    <>
                      <span className="relative h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                      <span className="relative">Opening WhatsApp...</span>
                    </>
                  ) : (
                    <>
                      <span className="relative">
                        Continue on WhatsApp
                      </span>

                      <FaWhatsapp className="relative text-lg transition duration-300 group-hover:scale-110" />
                    </>
                  )}
                </button>

                <p className="text-center text-[11px] leading-5 text-slate-400">
                  Your details will be used to prepare a WhatsApp message.
                  Nothing is submitted to a third-party form service.
                </p>
              </form>
            </div>
          </div>
        </section>

        {/* Map */}
        <section
          data-aos="fade-up"
          className="mt-8 overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-[0_20px_60px_rgba(15,23,42,.07)]"
        >
          <div className="flex flex-col gap-3 border-b border-slate-100 px-5 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-7">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                <FaMapMarkerAlt />
              </span>

              <div>
                <p className="text-sm font-extrabold text-slate-800">
                  Find PrimeAppConnect
                </p>

                <p className="mt-0.5 text-xs text-slate-400">
                  Our location
                </p>
              </div>
            </div>

            <a
              href="https://maps.app.goo.gl/UdfLXDpf4Nyc2B699"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-xs font-extrabold text-blue-600 transition hover:text-blue-700"
            >
              Get Directions
              <FaArrowRight className="text-[9px]" />
            </a>
          </div>

          <div className="relative overflow-hidden">
            <iframe
              title="PrimeAppConnect Location"
              src="https://www.google.com/maps?q=13.559823,78.503698&z=15&output=embed"
              className="h-[300px] w-full border-0 grayscale-[0.25] transition duration-500 hover:grayscale-0 sm:h-[380px]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />

            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-blue-950/10 to-transparent" />
          </div>
        </section>

        {/* Final CTA */}
        <section
          data-aos="zoom-in"
          className="relative mt-8 overflow-hidden rounded-[2rem] bg-blue-600 px-6 py-14 text-center shadow-[0_25px_70px_rgba(37,99,235,.20)] sm:px-10"
        >
          {/* CTA background */}
          <div className="absolute -left-24 -top-24 h-64 w-64 animate-pulse rounded-full bg-white/10 blur-3xl" />

          <div
            className="absolute -bottom-24 -right-24 h-64 w-64 rounded-full bg-cyan-300/20 blur-3xl"
            style={{ animation: "pulse 6s ease-in-out infinite" }}
          />

          {/* Decorative stars */}
          <FaStar className="absolute left-[12%] top-[25%] animate-pulse text-sm text-white/30" />
          <FaStar className="absolute right-[15%] top-[20%] animate-bounce text-xs text-white/25" />
          <FaStar className="absolute bottom-[25%] left-[20%] animate-pulse text-xs text-white/20" />

          <div className="relative">
            <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-xl bg-white/10 text-blue-100 transition duration-300 hover:rotate-12 hover:scale-110">
              <FaPaperPlane />
            </div>

            <p className="mt-5 text-[10px] font-extrabold tracking-[0.22em] text-blue-100">
              READY WHEN YOU ARE
            </p>

            <h2 className="mt-3 text-2xl font-extrabold tracking-tight text-white sm:text-3xl">
              Have Something in Mind?
            </h2>

            <p className="mx-auto mt-3 max-w-xl text-sm leading-7 text-blue-100">
              Let's turn your idea into a digital product that actually helps
              your business grow.
            </p>

            <Link
              to="/services"
              className="group mt-7 inline-flex items-center gap-2 rounded-xl bg-white px-7 py-3.5 text-sm font-extrabold text-blue-600 shadow-lg transition duration-300 hover:-translate-y-1 hover:shadow-xl"
            >
              Explore Our Services
              <FaArrowRight className="transition duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </section>
      </div>
    </main>
  );
}